// react
import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

// components
import { ShortlistDetailPage } from '@karehero/llama/pwa/pages/ShortlistDetailPage/ShortlistDetailPage';
import { ProviderDrawer } from '@karehero/llama/pwa/pages/ShortlistDetailPage/ProviderDrawer';
import { ContactSheet } from '@karehero/llama/pwa/pages/ShortlistDetailPage/ContactSheet';

// store
import {
  useGetPublishedShortlistQuery,
  useGetPublishedShortlistProvidersQuery,
  useUpdateShortlistItemStatusMutation,
  useUpdateShortlistStatusMutation,
} from 'store/api/shortlist';
import { newToast } from 'store/slices/uiSlice';

// hooks
import { useCareCircle, TrackingEvent, useTracking } from 'hooks';
import { useGoBack } from 'hooks/useGoBack';

// helpers
import { formatDate } from '@karehero/helpers/datetime';

// models
import { ShortlistItem, SHORTLIST_ITEM_STATUS } from '@karehero/models';

export const ShortlistDetail = () => {
  const { shortlistID } = useParams<{ shortlistID: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const goBack = useGoBack();
  const dispatch = useDispatch();
  const { careCircleId } = useCareCircle();
  const { track } = useTracking();

  const [updateItemStatus] = useUpdateShortlistItemStatusMutation();
  const [updateShortlistStatus] = useUpdateShortlistStatusMutation();

  const providerParam = searchParams.get('provider');
  const contactParam = searchParams.get('contact') === 'true';

  const { data: shortlist } = useGetPublishedShortlistQuery(
    { careCircleID: careCircleId || '', id: shortlistID || '' },
    { skip: !careCircleId || !shortlistID },
  );

  const { data: providers } = useGetPublishedShortlistProvidersQuery(
    { careCircleID: careCircleId || '', shortlistID: shortlistID || '' },
    { skip: !careCircleId || !shortlistID },
  );

  const selectedItem = useMemo(
    () => providers?.find((p) => p.regulatoryId === providerParam) ?? null,
    [providers, providerParam],
  );

  useEffect(() => {
    if (shortlist) {
      track(TrackingEvent.ShortlistViewed, { shortlistID: shortlist.id });
    }
  }, [shortlist, track]);

  const subtitle = useMemo(() => {
    const count = providers?.length ?? 0;
    const date = shortlist?.updatedAt || shortlist?.createdAt;
    const dateStr = date ? formatDate(date, { omitDay: true }) : '';
    return `${count} ${count === 1 ? 'provider' : 'providers'}${dateStr ? ` · Updated ${dateStr}` : ''}`;
  }, [providers, shortlist]);

  const openProvider = (item: { regulatoryId: string }) => {
    setSearchParams({ provider: item.regulatoryId }, { replace: true });
  };

  const closeProvider = () => {
    setSearchParams({}, { replace: true });
  };

  const openContact = () => {
    setSearchParams(
      { provider: providerParam || '', contact: 'true' },
      { replace: true },
    );
  };

  const closeContact = () => {
    setSearchParams({ provider: providerParam || '' }, { replace: true });
  };

  const handleMarkSecured = async (item: ShortlistItem) => {
    if (!careCircleId || !shortlistID) return;
    const secured = item.status !== SHORTLIST_ITEM_STATUS.SECURED;
    try {
      await updateItemStatus({
        careCircleID: careCircleId,
        shortlistID,
        regulatoryID: item.regulatoryId,
        secured,
      }).unwrap();
      track(TrackingEvent.ShortlistItemSecured, {
        shortlistID,
        regulatoryId: item.regulatoryId,
        providerName: item.provider.name,
        secured,
      });
    } catch (err) {
      Sentry.captureException(err);
      dispatch(
        newToast({
          title: 'Failed to update provider status',
          variant: 'error',
        }),
      );
    }
  };

  const handleSecuredElsewhere = async () => {
    if (!careCircleId || !shortlistID) return;
    try {
      await updateShortlistStatus({
        careCircleID: careCircleId,
        shortlistID,
        securedElsewhere: true,
      }).unwrap();
      track(TrackingEvent.ShortlistSecuredElsewhere, {
        shortlistID,
        shortlistName: shortlist?.name,
        careCircleId,
      });
    } catch (err) {
      Sentry.captureException(err);
      dispatch(
        newToast({
          title: 'Failed to update shortlist status',
          variant: 'error',
        }),
      );
    }
  };

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      dispatch(newToast({ title: `${label} copied`, variant: 'success' }));
    } catch (err) {
      Sentry.captureException(err);
      dispatch(
        newToast({
          title: `Couldn't copy the ${label.toLowerCase()}`,
          variant: 'error',
        }),
      );
    }
  };

  return (
    <>
      <ShortlistDetailPage
        shortlistName={shortlist?.name || ''}
        subtitle={subtitle}
        items={providers || []}
        onProviderClick={openProvider}
        onMarkSecured={handleMarkSecured}
        onBack={goBack}
        onSecuredElsewhere={handleSecuredElsewhere}
      />
      <ProviderDrawer
        item={selectedItem}
        onClose={closeProvider}
        onMarkSecured={handleMarkSecured}
        onBookVisit={openContact}
        onCopy={handleCopy}
      />
      <ContactSheet
        isOpen={contactParam && !!selectedItem}
        onClose={closeContact}
        phone={selectedItem?.provider.enquiryPhone}
        email={selectedItem?.provider.enquiryEmail}
      />
    </>
  );
};

export default ShortlistDetail;
